import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Egg } from 'lucide-react';
import { cn } from '../../utils/cn';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Instructions', path: '#instructions', isHash: true },
    { label: 'Recipes', path: '/recipes', newTab: true },
    { label: 'Contact', path: '/contact', newTab: true },
  ];

  const handleNavClick = (isHash: boolean | undefined, path: string) => {
    if (isHash) {
      const element = document.querySelector(path);
      element?.scrollIntoView({ behavior: 'smooth' });
    } else if (path === '/') {
      // For home link, always scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-[#FFD700]">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-black hover:opacity-80 transition-opacity"
            onClick={() => handleNavClick(false, '/')}
          >
            <div className="w-12 h-12 relative">
              <Egg className="w-12 h-12 text-black transform rotate-12" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-black rounded-full opacity-20"></div>
            </div>
            <span className="font-display text-xl font-bold">Egg Time On</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <nav className="flex items-center space-x-8">
              {navItems.map((item) => (
                item.newTab ? (
                  <a
                    key={item.path}
                    href={item.path}
                    target="_blank"
                    className="font-medium text-black hover:opacity-70 transition-opacity"
                  >
                    {item.label}
                  </a>
                ) : (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => handleNavClick(item.isHash, item.path)}
                    className={({ isActive }) =>
                      cn(
                        "font-medium text-black hover:opacity-70 transition-opacity",
                        isActive && "font-semibold"
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                )
              ))}
            </nav>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-black hover:opacity-70 focus:outline-none"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden bg-white border-t border-black/10 py-3">
            <div className="container mx-auto px-4 flex flex-col space-y-3">
              {navItems.map((item) => (
                item.newTab ? (
                  <a
                    key={item.path}
                    href={item.path}
                    target="_blank"
                    className="font-medium py-2 px-4 rounded-md text-black hover:bg-black/5 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                ) : (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => handleNavClick(item.isHash, item.path)}
                    className={({ isActive }) =>
                      cn(
                        "font-medium py-2 px-4 rounded-md text-black hover:bg-black/5 transition-colors",
                        isActive && "bg-black/5 font-semibold"
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                )
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;